import { productClusterList } from "store/ai-cluster/AiClusterType";
import { ProductSequenceData } from "../store/product-list/ProductListTypes";
import { SmartSortValues, SmartSortingRulesList } from "./Constants";
import { Product } from "../store/product/ProductTypes";
import { isListPriceGreaterThanPrice } from "./ProductUtil";
import { SMART_SORT_RULE_IDS } from "./Constants";

interface EnhancedProductData extends ProductSequenceData {
  cluster_score: number;
  rank: string;
  stock: number | null;
  launchDate: Date | null;
}

const createSimilarityMap = (
  productSimilarity: productClusterList[],
): Map<string, productClusterList> => {
  return new Map(productSimilarity.map((item) => [item.productId, item]));
};

const enhanceProduct = (
  product: ProductSequenceData,
  similarityData: productClusterList | undefined,
  productStock: number | null,
  productLaunchDate: Date | null,
): EnhancedProductData => {
  return {
    ...product,
    cluster_score: similarityData?.cluster_score ?? 0,
    rank: similarityData?.rank ?? "neutral",
    stock: productStock,
    launchDate: productLaunchDate,
  };
};

const partitionAndSortProducts = (
  products: EnhancedProductData[],
  filterCondition: (product: EnhancedProductData) => boolean,
  isReversed: boolean = false,
  sortByDate: boolean = false,
  customSortFn?: (a: EnhancedProductData, b: EnhancedProductData) => number,
): {
  updatedProductList: ProductSequenceData[];
  matchingIds: string[];
} => {
  // Single push-loop instead of reduce-with-spread (O(N) instead of O(N²)).
  const matchingProducts: EnhancedProductData[] = [];
  const otherProducts: EnhancedProductData[] = [];
  for (const product of products) {
    (filterCondition(product) ? matchingProducts : otherProducts).push(product);
  }

  if (customSortFn) {
    matchingProducts.sort(customSortFn);
  } else if (sortByDate) {
    matchingProducts.sort((a, b) => {
      const dateA = a.launchDate ? new Date(a.launchDate).getTime() : 0;
      const dateB = b.launchDate ? new Date(b.launchDate).getTime() : 0;
      return dateB - dateA;
    });
  } else {
    matchingProducts.sort((a, b) => b.cluster_score - a.cluster_score);
  }

  const updatedProductList = isReversed
    ? [...otherProducts, ...matchingProducts]
    : [...matchingProducts, ...otherProducts];
  const matchingIds = matchingProducts.map((p) => p.productId);

  return { updatedProductList, matchingIds };
};

const isNewProduct = (product: EnhancedProductData): boolean => {
  if (!product.launchDate) return false;
  const launchDate = new Date(product.launchDate);
  const currentDate = new Date();
  const diffTime = Math.abs(currentDate.getTime() - launchDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
};

const isDiscountedProduct = (
  product: EnhancedProductData,
  allProducts: { [key: string]: Product },
): boolean => {
  const productData = allProducts[product.productId];
  if (!productData) return false;

  const listPrice = productData.listPrice?.toString() || null;
  const lowPrice = productData.lowPrice;
  const highPrice = productData.highPrice;

  let priceRange: number[] | null = null;
  if (lowPrice !== undefined && highPrice !== undefined) {
    priceRange = [lowPrice, highPrice];
  } else if (lowPrice !== undefined) {
    priceRange = [lowPrice];
  }

  return isListPriceGreaterThanPrice(listPrice, priceRange);
};

const sortDiscountedProducts = (
  productsWithScores: EnhancedProductData[],
  allProducts: { [key: string]: Product },
  isReversed: boolean = true,
): { updatedProductList: ProductSequenceData[]; matchingIds: string[] } => {
  const discountedProductPrices = new Map<string, number>();

  productsWithScores.forEach((product) => {
    if (isDiscountedProduct(product, allProducts)) {
      const productData = allProducts[product.productId];
      const lowPrice = productData.lowPrice;
      const highPrice = productData.highPrice;

      const discountedPrice =
        lowPrice !== undefined
          ? lowPrice
          : highPrice !== undefined
            ? highPrice
            : 0;
      discountedProductPrices.set(product.productId, discountedPrice);
    }
  });

  return partitionAndSortProducts(
    productsWithScores,
    (product) => discountedProductPrices.has(product.productId),
    isReversed,
    false,
    (a, b) =>
      (discountedProductPrices.get(b.productId) || 0) -
      (discountedProductPrices.get(a.productId) || 0),
  );
};

// Get Updated Product List
export const getUpdatedProductList = (
  products: ProductSequenceData[],
  productSimilarity: productClusterList[],
  allProducts: { [key: string]: Product },
  sortType: string,
): {
  updatedProductList: ProductSequenceData[];
  promotedProducts?: string[];
  demotedProducts?: string[];
} => {
  const similarityMap = createSimilarityMap(productSimilarity);
  const productsWithScores: EnhancedProductData[] = products.map((product) =>
    enhanceProduct(
      product,
      similarityMap.get(product.productId),
      allProducts[product.productId]?.stock ?? null,
      allProducts[product.productId]?.launchDate ?? null,
    ),
  );
  let updatedProductList: ProductSequenceData[] = [...products];
  let promotedProducts: string[] = [];
  let demotedProducts: string[] = [];

  // sorting based on sortType. Each case partitions in a single pass — the
  // matchingIds returned by partitionAndSortProducts replaces the previous
  // pre-partition .filter().map() walk over productsWithScores.
  switch (sortType) {
    case SmartSortValues.boost: {
      const partitioned = partitionAndSortProducts(
        productsWithScores,
        (product) => product.rank === SmartSortValues.boost,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        promotedProducts: partitioned.matchingIds,
      };
    }
    case SmartSortValues.bury: {
      const partitioned = partitionAndSortProducts(
        productsWithScores,
        (product) => product.rank === SmartSortValues.bury,
        true,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        demotedProducts: partitioned.matchingIds,
      };
    }
    case SmartSortValues.outOfStock: {
      const partitioned = partitionAndSortProducts(
        productsWithScores,
        (product) => product.stock === 0,
        true,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        demotedProducts: partitioned.matchingIds,
      };
    }
    case SmartSortValues.newProducts: {
      const partitioned = partitionAndSortProducts(
        productsWithScores,
        (product) => isNewProduct(product),
        false,
        true,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        promotedProducts: partitioned.matchingIds,
      };
    }
    case SmartSortValues.discountedProductsToTop: {
      const partitioned = sortDiscountedProducts(
        productsWithScores,
        allProducts,
        false,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        promotedProducts: partitioned.matchingIds,
      };
    }
    case SmartSortValues.discountedProductsToBottom: {
      const partitioned = sortDiscountedProducts(
        productsWithScores,
        allProducts,
      );
      return {
        updatedProductList: partitioned.updatedProductList,
        demotedProducts: partitioned.matchingIds,
      };
    }

    default:
      return { updatedProductList, promotedProducts, demotedProducts };
  }
};

/**
 * Checks if a rule ID is a smart sort rule ID (hardcoded rule IDs)
 * @param ruleId - The rule ID to check
 * @returns true if the rule ID is a smart sort rule ID
 */
export const isSmartSortRuleId = (ruleId: string): boolean => {
  return SmartSortingRulesList.some((smartRule) => smartRule.value === ruleId);
};

/**
 * Determines if a smart sort rule is a "move up" rule (promotes products) or "move down" rule (demotes products)
 * @param ruleId - The rule ID to check
 * @returns true if it's a move up rule, false if it's a move down rule
 */
export const isSmartSortPromoteRule = (ruleId: string): boolean => {
  return (
    ruleId === SMART_SORT_RULE_IDS.NEW || ruleId === SMART_SORT_RULE_IDS.BOOSTED || ruleId === SMART_SORT_RULE_IDS.DISCOUNTED_TO_TOP
  );
};
